import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Modal } from 'bootstrap';
import { CountdownComponent, CountdownEvent } from 'ngx-countdown';
import { AuthorizationService } from '@unleashed/services/account';
import { Booking, NavigationSteps } from '@unleashed/models/booking';
import { BookingSessionService } from '@unleashed/services/booking';
import {Subscription} from 'rxjs';

@Component({
  selector: 'ua-timer',
  templateUrl: './timer.component.html',
  styleUrls: ['./timer.component.scss']
})
export class TimerComponent implements OnInit, OnDestroy {
  isTimeBegan: boolean | undefined = false;
  timeBegan?: Date;
  timeLeft?: number;
  timerClass?: string;
  booking?: Booking;
  sessionExpiredModal?: Modal;
  timeoutId?: number;
  @ViewChild('cd', { static: false }) private countdown?: CountdownComponent;
  subscriptions = new Subscription();

  constructor(
    private sessionService: BookingSessionService,
    private authService: AuthorizationService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const element = document.getElementById('sessionExpiredModal');
    if (element) {
      this.sessionExpiredModal = new Modal(element);
    }

    this.subscriptions.add(
      this.sessionService.getBooking().subscribe(booking => {
        this.isTimeBegan = !!booking.timeBegan;
        this.timeBegan = booking.timeBegan;
        this.booking = booking;
        if (this.isTimeBegan && this.timeBegan) {
          this.setTimer(this.timeBegan);
        }
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  showSessionExpiredModal(): void {
    this.sessionExpiredModal?.show();
  }

  closeModal(): void {
    this.sessionExpiredModal?.hide();
  }

  handleEvent(event: CountdownEvent): void {
    switch (event.action) {
      case 'done': {
        if (!this.timeBegan) {
          return;
        }

        // STORE-203: cart timer is global to the session and would otherwise fire on
        // /confirmation, forcing a duplicate when the user re-books. Also clean up any
        // modal/timeout left over from a prior 'done' fire so the backdrop doesn't strand.
        if (this.sessionService.hasConfirmedBooking()) {
          this.closeModal();
          clearTimeout(this.timeoutId);
          return;
        }

        // Fixed 5-min grace from the moment the modal appears, regardless of how
        // stale `timeBegan` is. Anchoring to "now" instead of `timeBegan + 25` means
        // a user landing on a cart that's already past its 20-min mark still gets
        // the full window to extend or cancel.
        const fiveMinutesMs = 5 * 60 * 1000;
        this.showSessionExpiredModal();
        this.timeoutId = window.setTimeout(() => {
          // Re-check at fire time: a payment that completes during the 5-min grace
          // window shouldn't get bounced by the deferred kick. Close the modal that
          // was shown when we scheduled this timeout so it doesn't block the
          // confirmation page.
          if (this.sessionService.hasConfirmedBooking()) {
            this.closeModal();
            return;
          }
          this.authService.logout();
          this.goToStart();
        }, fiveMinutesMs);
        break;
      } case 'start':
        case 'notify': {
        let i = 0;
        let count = 0;
        for (i <= 0; i < 3; i ++) {
          setTimeout(() => {
            this.flashTimer(count);
            count ++;
          }, i * 2000);
        }
        break;
      }
    }
  }

  flashTimer(count: number): void {
    this.timerClass = 'text-primary';
    setTimeout(() => {
      this.timerClass = 'text-alt';
      if (count === 2) {
        this.timerClass = 'text-dark';
      }
    }, 1000);
  }

  setTimer(timeBegan: Date): void {
    const futureTimeFromTimeBegan = new Date(timeBegan);

    if (futureTimeFromTimeBegan.getTime() <= new Date().getTime()) {
      futureTimeFromTimeBegan.setMinutes(futureTimeFromTimeBegan.getMinutes() + 20);
    }

    this.timeLeft = (futureTimeFromTimeBegan.getTime() - new Date().getTime()) / 1000;
    this.countdown?.restart();
  }

  goToStart(): void {
    this.closeModal();
    clearTimeout(this.timeoutId);
    this.sessionService.removeSessionCart();
    this.sessionService.navigateToStep(NavigationSteps.Start, true);
  }

  extendTime(): void {
    this.closeModal();
    clearTimeout(this.timeoutId);
    if (!this.booking) {
      return;
    }
    this.booking.timeBegan = new Date();
    this.timeBegan = this.booking.timeBegan;
    this.sessionService.upsertCart(this.booking).subscribe();
    this.setTimer(this.timeBegan);
  }

}
